import './SourcesPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterItem } from '../../shared/components/filter-box/models/FilterBoxModel';
import Title from '../../shared/components/title/Title';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import { expandSelectedChatTypes, getMonitorTypesForChatType } from '../../shared/helpers/MonitorTypeHelpers';
import { ChatType } from '../../shared/types/monitor/ChatType';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useGetDomainsStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsStats';
import { useProjectDates } from '../../shared/hooks/filters/UseProjectDates';
import { useGetTypesStats } from '../../shared/api/hooks/monitor/data/sources/UseGetTypesStats';
import { useGetDomainsDailyStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsDailyStats';
import TypesShareBarChart from './components/types-share-bar-chart/TypesShareBarChart';
import { DomainsStatsTable } from './components/domains-stats-table/DomainsStatsTable';
import { useDomainsStatsComparison } from './hooks/UseDomainsStatsComparison';
import { useDomainsStatsShare } from './hooks/UseDomainsStatsShare';
import { useDomainsDailyStatsMatrix } from './hooks/UseDomainsDailyStatsMatrix';
import { useTypesShareHorizontalMatrix } from './hooks/UseTypesShareHorizontalMatrix';
import { useCitationTypesShareData } from './hooks/UseCitationTypesShareData';
import { useProjectDomain } from '../../shared/hooks/projects/preview/UseProjectDomain';
import DomainsDailyStatsChart from './components/domains-daily-stats-chart/DomainsDailyStatsChart';
import CitationTypesShareBarChart from './components/citation-types-share-bar-chart/CitationTypesShareBarChart';
import { useGetCategoriesStats } from '../../shared/api/hooks/monitor/data/sources/UseGetCategoriesStats';
import { useGetDomainsWithSourcesStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsWithSourcesStats';
import { DomainsWithSourcesStatsTable } from './components/domains-with-sources-stats-table/DomainsWithSourcesStatsTable';
import { useDomainsWithSourcesStatsComparison } from './hooks/UseDomainsWithSourcesStatsComparison';
import { useDomainsWithSourcesStatsShare } from './hooks/UseDomainsWithSourcesStatsShare';
import { useGetSourceStats } from '../../shared/api/hooks/monitor/data/sources/UseGetSourceStats';
import { useSourceStatsComparison } from './hooks/UseSourceStatsComparison';
import { useSourcesStatsShare } from './hooks/UseSourcesStatsShare';
import { SourcesStatsTable } from './components/sources-stats-table/SourcesStatsTable';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import { usePromptGroups } from '../../shared/hooks/prompt/UsePromptGroups';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { updateDomainClassification } from '../../shared/services/projects/ProjectService';
import ComparisonDateRangePicker from './components/comparison-date-range-picker/ComparisonDateRangePicker';
import { differenceInDays, format, subDays } from 'date-fns';
import { useGetCommonKeywords } from '../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { KeywordResponse } from '../../shared/models/monitor/KeywordResponse';
import { useGetTags } from '../../shared/api/hooks/tags/useGetTags';
import { getTagLabel } from '../../shared/helpers/tags/TagsHelper';
import { TAG_NAME } from '../../shared/utils/urls';
import { downloadBlobAsFile } from '../../shared/utils/utils';
import useExportData from '../../shared/hooks/projects/UseExportData';
import useExport from '../../shared/hooks/global/UseExport';
import DomainsPerModelSection from './components/domains-per-model/DomainsPerModelSection';
import WatchedPagesSection from './components/watched-pages/WatchedPagesSection';
import PageMovementSection from './components/page-movement/PageMovementSection';
import CompetitorSourcesSection from './components/competitor-sources/CompetitorSourcesSection';
import { isCompetitorDomain } from '../../shared/helpers/competitor/CompetitorHelper';
import {
  exportDomainsStats,
  getDomainsStats,
} from '../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { useQuery } from '@tanstack/react-query';

const SourcesPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { projectId } = useParams();
  const { selectedChatType, selectedChatTypes, monitorPeriod, dateRange } = useFilters();
  const { startDate, endDate } = useProjectDates();

  const [chartDomain, setChartDomain] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const [sourcesPage, setSourcesPage] = useState(1);
  const [sourcesPageSize, setSourcesPageSize] = useState(10);
  const [localSelectedDomains, setLocalSelectedDomains] = useState<string[]>([]);
  const [domainsWithSourcesPage, setDomainsWithSourcesPage] = useState(1);
  const [domainsWithSourcesPageSize, setDomainsWithSourcesPageSize] = useState(10);

  const [sourcesKeyword, setSourcesKeyword] = useState('');
  const [domainsWithSourcesKeyword, setDomainsWithSourcesKeyword] = useState('');

  const [comparisonRange, setComparisonRange] = useState<(Date | null)[]>([null, null]);
  const isComparisonRangeManuallySet = useRef<boolean>(false);

  const navigate = useNavigate();

  const projectIdNumber = Number(projectId);
  const projectDomain = useProjectDomain(projectIdNumber);
  const { accountResponse, projectResponse, setProjectResponse } = useAccount();
  const { canExport } = useExport();
  const { exportSources } = useExportData(projectIdNumber);

  useProjectNavigate(projectIdNumber);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = useMemo(() => {
    const param = searchParams.get('group');
    if (!param) return -1;
    const parsed = Number(param);
    return isNaN(parsed) ? -1 : parsed;
  }, [searchParams]);
  const setSelectedGroupId = useCallback((groupId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (groupId === -1) {
        next.delete('group');
      } else {
        next.set('group', String(groupId));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const { data: promptGroups } = usePromptGroups(projectIdNumber);
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || selectedGroupId === -1) return undefined;
    return monitorPrompts
      .filter((p) => p.promptGroupId === selectedGroupId)
      .map((p) => p.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, selectedGroupId]);

  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    const expandedChatTypes = expandSelectedChatTypes(selectedChatTypes);
    return Array.from(expandedChatTypes).flatMap(ct => getMonitorTypesForChatType(ct as ChatType));
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts
        .map((p) => p.promptGroupId)
        .filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const groupFilterOptions = useMemo(() => {
    if (!hasPromptGroupsAddon || nonEmptyPromptGroups.length === 0) return [];
    return [
      { label: t('allGroups'), value: -1 },
      ...nonEmptyPromptGroups.map((g) => ({ label: g.name, value: g.id })),
    ];
  }, [hasPromptGroupsAddon, nonEmptyPromptGroups, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
        setChartDomain(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate previous period dates based on comparison range
  const previousPeriodDates = useMemo(() => {
    if (!comparisonRange || !comparisonRange[0] || !comparisonRange[1]) {
      return null;
    }
    return {
      previousStartDate: comparisonRange[0],
      previousEndDate: comparisonRange[1],
    };
  }, [comparisonRange]);

  // Initialize comparison range based on main range only if it hasn't been manually set
  useEffect(() => {
    if (startDate && endDate && !isComparisonRangeManuallySet.current) {
      const periodLengthInDays = differenceInDays(endDate, startDate);
      const previousEndDate = subDays(startDate, 1);
      const previousStartDate = subDays(previousEndDate, periodLengthInDays);
      setComparisonRange([previousStartDate, previousEndDate]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setLocalSelectedDomains([]);
  }, [selectedGroupId]);

  const domainsStatsPageSize = useMemo(() => {
    return localSelectedDomains.length > 0 ? Math.max(localSelectedDomains.length, 5) : 5;
  }, [localSelectedDomains]);

  const selectedDomainsForQuery = useMemo(() => {
    return localSelectedDomains.length > 0 ? localSelectedDomains : undefined;
  }, [localSelectedDomains]);

  const {
    domainsStats,
    isLoading: isDomainsStatsLoading,
    refetch: refetchDomainsStats,
  } = useGetDomainsStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    1,
    domainsStatsPageSize,
    selectedDomainsForQuery,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsStats: previousDomainsStats,
    isLoading: isPreviousDomainsStatsLoading,
    refetch: refetchPreviousDomainsStats,
  } = useGetDomainsStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    1,
    domainsStatsPageSize,
    selectedDomainsForQuery,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { data: allDomainsData, isLoading: isAllDomainsLoading } = useQuery({
    queryKey: [
      'all-domains-filter',
      projectIdNumber,
      selectedChatType,
      startDate?.getTime(),
      endDate?.getTime(),
      filteredMonitorPromptIds,
      filterMonitorTypes,
    ],
    queryFn: () =>
      getDomainsStats(projectIdNumber, false, selectedChatType!, startDate, endDate, 1, 100, undefined, undefined, filteredMonitorPromptIds, filterMonitorTypes),
    enabled: !!projectIdNumber && !!startDate && !!endDate && !!selectedChatType,
    staleTime: 1000 * 60 * 5,
  });

  const {
    typesStats,
    isLoading: isTypesStatsLoading,
    refetch: refetchTypesStats,
  } = useGetTypesStats(projectIdNumber, selectedChatType, startDate, endDate, undefined, filteredMonitorPromptIds, filterMonitorTypes);

  const {
    domainsDailyStats,
    isLoading: isDomainsDailyStatsLoading,
    refetch: refetchDomainsDailyStats,
  } = useGetDomainsDailyStats(
    projectIdNumber,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    selectedDomainsForQuery,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsWithSourcesStats,
    isLoading: isDomainsWithSourcesStatsLoading,
    refetch: refetchDomainsWithSourcesStats,
  } = useGetDomainsWithSourcesStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    domainsWithSourcesPage,
    domainsWithSourcesPageSize,
    undefined,
    domainsWithSourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsWithSourcesStats: previousDomainsWithSourcesStats,
    isLoading: isPreviousDomainsWithSourcesStatsLoading,
    refetch: refetchPreviousDomainsWithSourcesStats,
  } = useGetDomainsWithSourcesStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    domainsWithSourcesPage,
    domainsWithSourcesPageSize,
    undefined,
    domainsWithSourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const domainsStatsWithShare = useDomainsStatsShare(domainsStats);
  const previousDomainsStatsWithShare = useDomainsStatsShare(previousDomainsStats);

  const domainsStatsComparison = useDomainsStatsComparison(
    domainsStatsWithShare,
    previousDomainsStatsWithShare
  );

  const filteredDomainsStatsComparison = useMemo(() => {
    if (!domainsStatsComparison || localSelectedDomains.length === 0) {
      return domainsStatsComparison;
    }

    const filtered = domainsStatsComparison
      .filter((domain) => localSelectedDomains.includes(domain.domain))
      .sort((a, b) => b.currentCount - a.currentCount);

    return filtered;
  }, [domainsStatsComparison, localSelectedDomains]);

  const citationTrendsMatrix = useDomainsDailyStatsMatrix(
    domainsDailyStats,
    domainsStatsWithShare,
    projectDomain,
    selectedDomainsForQuery
  );

  const typesShareMatrix = useTypesShareHorizontalMatrix(typesStats);

  const hasSourceCategories = accountResponse?.addons?.includes('SOURCES_3_0') ?? false;
  const { categoriesStats } = useGetCategoriesStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    hasSourceCategories,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { shouldShow: shouldShowCategoriesChart } =
    useCitationTypesShareData(categoriesStats, hasSourceCategories);

  const domainsWithSourcesStatsWithShare = useDomainsWithSourcesStatsShare(domainsWithSourcesStats);
  const previousDomainsWithSourcesStatsWithShare = useDomainsWithSourcesStatsShare(
    previousDomainsWithSourcesStats
  );

  const domainsWithSourcesStatsComparison = useDomainsWithSourcesStatsComparison(
    domainsWithSourcesStatsWithShare,
    previousDomainsWithSourcesStatsWithShare
  );

  const {
    sourcesStats,
    isLoading: isSourcesStatsLoading,
    refetch: refetchSourcesStats,
  } = useGetSourceStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    sourcesPage,
    sourcesPageSize,
    undefined,
    sourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    sourcesStats: previousSourcesStats,
    isLoading: isPreviousSourcesStatsLoading,
    refetch: refetchPreviousSourcesStats,
  } = useGetSourceStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    sourcesPage,
    sourcesPageSize,
    undefined,
    sourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const sourcesStatsWithShare = useSourcesStatsShare(sourcesStats);
  const previousSourcesStatsWithShare = useSourcesStatsShare(previousSourcesStats);

  const sourcesStatsComparison = useSourceStatsComparison(
    sourcesStatsWithShare,
    previousSourcesStatsWithShare
  );

  const { commonKeywords } = useGetCommonKeywords(projectIdNumber);

  const { tags } = useGetTags(projectIdNumber);

  const currentProject = useMemo(
    () => projectResponse?.find((p) => p.id === projectIdNumber),
    [projectResponse, projectIdNumber]
  );

  const firmName = useMemo(() => {
    if (!currentProject) return '';
    const rawFirmName = currentProject.firmName || '';
    if (!rawFirmName.startsWith(TAG_NAME)) {
      return rawFirmName;
    }
    const tagId = Number(rawFirmName.slice(TAG_NAME.length)) || null;
    return getTagLabel(tags, tagId, rawFirmName);
  }, [currentProject, tags]);

  const competition = useMemo(() => currentProject?.competition ?? [], [currentProject]);

  const competitorKeywords = useMemo(() => {
    if (!commonKeywords?.length || !firmName) return [];
    const firmNorm = firmName.toLowerCase().trim();
    return commonKeywords.filter((k) => k.normalizedKeyword.toLowerCase() !== firmNorm);
  }, [commonKeywords, firmName]);

  const competitorKeywordNames = useMemo(
    () => competitorKeywords.map((k) => k.normalizedKeyword),
    [competitorKeywords]
  );

  const domainClassification = currentProject?.domainClassification || null;

  const competitorCount = useMemo(() => {
    if (!allDomainsData?.domains?.length) return 0;
    return allDomainsData.domains
      .filter((d) => {
        const override = domainClassification?.[d.domain];
        if (override) return override === 'COMPETITOR';
        return isCompetitorDomain(d.domain, competitorKeywords, competition);
      })
      .reduce((sum, d) => sum + d.count, 0);
  }, [allDomainsData, competition, competitorKeywords, domainClassification]);

  const { chartData: categoriesChartData } = useCitationTypesShareData(
    categoriesStats, hasSourceCategories, hasSourceCategories ? competitorCount : 0
  );

  const handleUpdateDomainClassification = useCallback(
    (classification: Record<string, string>) => {
      if (!projectIdNumber) return;
      setProjectResponse((prev) =>
        prev
          ? prev.map((p) =>
              p.id === projectIdNumber ? { ...p, domainClassification: classification } : p
            )
          : prev
      );
      void updateDomainClassification(projectIdNumber, classification);
    },
    [projectIdNumber, setProjectResponse]
  );

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) {
      return [];
    }
    const replaceLastTwoSegments = (url: string, period: string, chat: string): string => {
      const parts = url.split('/').filter(Boolean);
      const newParts = parts.slice(0, -2);
      return `/${newParts.join('/')}/${period}/${chat}`;
    };
    const chat = selectedChatType.toLowerCase();
    return [
      {
        url: replaceLastTwoSegments(location.pathname, 'daily', chat),
        label: t('dailyFilter'),
        isActive: monitorPeriod === 'DAILY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'weekly', chat),
        label: t('weeklyFilter'),
        isActive: monitorPeriod === 'WEEKLY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'monthly', chat),
        label: t('monthlyFilter'),
        isActive: monitorPeriod === 'MONTHLY',
      },
    ];
  }, [location.pathname, selectedChatType, monitorPeriod, t]);

  const handleComparisonRangeChange = (newRange: Date[]): void => {
    isComparisonRangeManuallySet.current = true;
    setComparisonRange(newRange);
  };

  const handleExportDomainsStats = async (): Promise<void> => {
    if (!canExport || !startDate || !endDate || !selectedChatType) {
      return;
    }

    const blob = await exportDomainsStats(projectIdNumber, selectedChatType, startDate, endDate);
    const fileName = `domains-${selectedChatType}-${format(startDate, 'yyyy-MM-dd')}-${format(
      endDate,
      'yyyy-MM-dd'
    )}.csv`;
    downloadBlobAsFile(blob, fileName);
  };

  if (!accountResponse || !accountResponse.addons.includes('SOURCES_STATS') || !projectId) {
    navigate(PROJECTS);
    return <></>;
  }

  return (
    <>
      <div className="sources-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={t('sources')}
              children={
                <div className="filters">
                  <FilterPanel baseUrl={location.pathname} showPeriodTabs={false}>
                    {groupFilterOptions.length > 0 && (
                      <Dropdown
                        value={selectedGroupId}
                        onChange={(e) => setSelectedGroupId(e.value)}
                        options={groupFilterOptions}
                        optionLabel="label"
                        optionValue="value"
                        className="dropdown dropdown--group-filter"
                        valueTemplate={(option) => (
                          <>
                            <span id={`group-filter-val-sources-${option?.value}`} className="dropdown--group-filter__label">
                              {option?.label}
                            </span>
                            {option?.label && (
                              <Tooltip target={`#group-filter-val-sources-${option.value}`} content={option.label} position="top" showDelay={300} />
                            )}
                          </>
                        )}
                        itemTemplate={(option) => (
                          <>
                            <span id={`group-filter-item-sources-${option.value}`} className="dropdown--group-filter__label">
                              {option.label}
                            </span>
                            <Tooltip target={`#group-filter-item-sources-${option.value}`} content={option.label} position="top" showDelay={300} />
                          </>
                        )}
                      />
                    )}
                    <ComparisonDateRangePicker
                      value={comparisonRange}
                      onChange={handleComparisonRangeChange}
                      referenceStartDate={startDate}
                      referenceEndDate={endDate}
                      minDate={dateRange?.oldestDate}
                      maxDate={dateRange?.newestDate}
                    />
                  </FilterPanel>
                </div>
              }
              showStatusDot={true}
            />
          </div>
          <div className="page-box__stats">
            <div className="stats stats--full-width">
              <DomainsDailyStatsChart
                matrix={citationTrendsMatrix}
                selectedDomain={chartDomain}
                domainsStatsWithShare={domainsStatsWithShare}
                isLoading={isDomainsDailyStatsLoading}
                periodFilterItems={periodFilterItems}
                period={monitorPeriod}
              />
            </div>
            {accountResponse?.addons?.includes('SOURCES_3_0') && (
              <div className="stats stats--full-width">
                <DomainsPerModelSection
                  projectId={projectIdNumber}
                  startDate={startDate}
                  endDate={endDate}
                  features={accountResponse?.features || []}
                  selectedChatType={selectedChatType}
                  monitorPromptIds={filteredMonitorPromptIds}
                  selectedChatTypes={hasExplicitModelFilter ? selectedChatTypes : undefined}
                  filterMonitorTypes={filterMonitorTypes}
                />
              </div>
            )}
            {!hasSourceCategories && (
              <div className="stats stats--full-width">
                <div ref={tableRef}>
                  <DomainsStatsTable
                    domainsStats={filteredDomainsStatsComparison}
                    selectedDomain={chartDomain}
                    onDomainSelect={setChartDomain}
                    startDate={startDate}
                    endDate={endDate}
                    previousStartDate={previousPeriodDates?.previousStartDate || null}
                    previousEndDate={previousPeriodDates?.previousEndDate || null}
                    isLoading={isDomainsDailyStatsLoading || isPreviousDomainsStatsLoading}
                    selectedDomains={localSelectedDomains}
                    projectId={projectIdNumber}
                    chatType={selectedChatType!}
                    onDomainsChange={setLocalSelectedDomains}
                  />
                </div>
              </div>
            )}
            {shouldShowCategoriesChart && (
              <div className="stats stats--full-width">
                <CitationTypesShareBarChart chartData={categoriesChartData} />
              </div>
            )}

            {!hasSourceCategories && selectedChatType === 'ALL' && typesShareMatrix.length > 0 && (
              <div className="stats stats--scroll stats--full-width">
                <TypesShareBarChart typesShareMatrix={typesShareMatrix ?? []} />
              </div>
            )}

            <DomainsWithSourcesStatsTable
              domains={domainsWithSourcesStatsComparison?.domains || []}
              pagination={domainsWithSourcesStatsComparison?.pagination}
              keyword={domainsWithSourcesKeyword}
              commonKeywords={commonKeywords as unknown as KeywordResponse[]}
              tags={tags}
              startDate={startDate}
              endDate={endDate}
              previousStartDate={previousPeriodDates?.previousStartDate || null}
              previousEndDate={previousPeriodDates?.previousEndDate || null}
              onKeywordChange={(keyword) => {
                setDomainsWithSourcesKeyword(keyword);
                setDomainsWithSourcesPage(1); // Reset to first page when keyword changes
              }}
              onPageChange={(page, pageSize) => {
                setDomainsWithSourcesPage(page);
                setDomainsWithSourcesPageSize(pageSize);
              }}
              isLoading={isDomainsWithSourcesStatsLoading || isPreviousDomainsWithSourcesStatsLoading}
              hasAdvancedFeatures={accountResponse?.addons.includes('SOURCES_STATS_ADVANCED') ?? false}
              firmName={firmName}
              canExport={canExport}
              onExport={handleExportDomainsStats}
              hasDomainDetailPage={hasSourceCategories}
            />
            <SourcesStatsTable
              sources={sourcesStatsComparison?.sources || []}
              pagination={sourcesStatsComparison?.pagination}
              commonKeywords={commonKeywords as unknown as KeywordResponse[]}
              tags={tags}
              isLoading={isSourcesStatsLoading || isPreviousSourcesStatsLoading}
              keyword={sourcesKeyword}
              startDate={startDate}
              endDate={endDate}
              previousStartDate={previousPeriodDates?.previousStartDate || null}
              previousEndDate={previousPeriodDates?.previousEndDate || null}
              onKeywordChange={(keyword) => {
                setSourcesKeyword(keyword);
                setSourcesPage(1);
              }}
              onPageChange={(page, pageSize) => {
                setSourcesPage(page);
                setSourcesPageSize(pageSize);
              }}
              hasAdvancedFeatures={accountResponse?.addons.includes('SOURCES_STATS_ADVANCED') ?? false}
              firmName={firmName}
              onExport={exportSources}
              hasSourceDetailPage={hasSourceCategories}
            />
            {accountResponse?.addons?.includes('SOURCES_3_0') && (
              <>
                <div className="stats stats--full-width">
                  <CompetitorSourcesSection
                    allDomainsData={allDomainsData}
                    projectDomain={projectDomain || null}
                    projectWebsite={currentProject?.website || null}
                    competitorKeywords={competitorKeywords}
                    competition={competition}
                    isLoading={isAllDomainsLoading}
                    domainClassification={currentProject?.domainClassification || null}
                    onUpdateDomainClassification={handleUpdateDomainClassification}
                    isSuperAdmin={accountResponse?.isSuperAdmin ?? false}
                  />
                </div>
                {projectDomain && (
                  <div className="stats stats--full-width">
                    <PageMovementSection
                      projectId={projectIdNumber}
                      domain={projectDomain}
                      chatType={selectedChatType}
                      from={startDate}
                      to={endDate}
                      monitorPromptIds={filteredMonitorPromptIds}
                      filterMonitorTypes={filterMonitorTypes}
                    />
                  </div>
                )}
                <div className="stats stats--full-width">
                  <WatchedPagesSection
                    projectId={projectIdNumber}
                    firmName={firmName}
                    chatType={selectedChatType || 'ALL'}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SourcesPage;
